import BottomSheet from '@gorhom/bottom-sheet';
import i18n from '@/utils/i18n';
import React, { useEffect, useRef } from 'react';
import {
  AffordanceHandleIndicator,
  BottomSheetBackdrop,
} from '@/components/bottom-sheet-backdrop';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { CannedQuestionList } from './canned-question-list';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Keyboard, Modal, ScrollView, Text, View } from 'react-native';
import { MAX_CANNED_QUESTIONS } from '@/contexts/canned-questions-context';
import { useBottomSheetBackHandler } from '@/hooks/use-bottom-sheet-back-handler';
import { useLayoutContext } from '@/contexts/layout-context';

const CannedSheetTitle = () => {
  const [styles] = useStyles();
  return (
    <View>
      <Text style={[TYPOGRAPHY.h2, FONTS.primarySemiBold, styles.title]}>
        {i18n.t('casey_ai.canned_question_action_sheet.title')}
      </Text>
      <Text style={[TYPOGRAPHY.p, styles.subtitle]}>
        {i18n.t('casey_ai.canned_question_action_sheet.subtitle')}
      </Text>
    </View>
  );
};

interface Props {
  isVisible: boolean;
  onClose: () => void;
  onSelect: (question: string) => void;
  renderInDraggableSheet: boolean;
}

export const CannedQuestionActionSheet: React.FC<Props> = ({
  isVisible,
  onClose,
  onSelect,
  renderInDraggableSheet,
}) => {
  const bottomSheetRef = useRef<BottomSheet>(null);
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();
  const { handleSheetPositionChange } =
    useBottomSheetBackHandler(bottomSheetRef);

  useEffect(() => {
    // dismiss keyboard when action sheet opens
    if (isVisible) {
      Keyboard.dismiss();
    }
  }, [isVisible]);

  useEffect(() => {
    /* Implemented this onDismount routine to ensure bottomSheet
     * is not kept opened when it's slid down but remains open,
     * and then cannot be popped up again
     */
    () => {
      bottomSheetRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      bottomSheetRef.current?.expand();
    }
  }, [isVisible]);

  return renderInDraggableSheet ? (
    <Modal
      animationType="fade"
      onRequestClose={onClose}
      transparent
      visible={isVisible}
    >
      <GestureHandlerRootView style={styles.gestureHandler}>
        <BottomSheet
          android_keyboardInputMode="adjustResize"
          backdropComponent={BottomSheetBackdrop}
          enableContentPanningGesture={false}
          enablePanDownToClose
          handleComponent={AffordanceHandleIndicator}
          index={1}
          keyboardBehavior="extend"
          keyboardBlurBehavior="restore"
          onChange={handleSheetPositionChange}
          onClose={onClose}
          ref={bottomSheetRef}
          snapPoints={['65%', '85%']}
        >
          <ScrollView
            contentContainerStyle={styles.modalContent}
            showsVerticalScrollIndicator={false}
          >
            <CannedSheetTitle />
            <CannedQuestionList
              maxSize={MAX_CANNED_QUESTIONS}
              numColumns={2}
              onQuestionSelected={(question) => {
                onSelect(question);
                onClose();
              }}
            />
          </ScrollView>
        </BottomSheet>
      </GestureHandlerRootView>
    </Modal>
  ) : (
    <BaseActionSheet
      contentStyle={[
        styles.modalContainer,
        isSmallLayout ? styles.webMobileMaxHeight : null,
      ]}
      isVisible={isVisible}
      onClose={onClose}
      title={<CannedSheetTitle />}
    >
      <ScrollView contentContainerStyle={styles.modalContent}>
        {isSmallLayout ? <CannedSheetTitle /> : null}
        <CannedQuestionList
          maxSize={MAX_CANNED_QUESTIONS}
          numColumns={2}
          onQuestionSelected={(question) => {
            onSelect(question);
            onClose();
          }}
        />
      </ScrollView>
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  gestureHandler: {
    flex: 1,
  },
  modalContainer: {
    paddingHorizontal: 8,
  },
  modalContent: {
    gap: 16,
    paddingHorizontal: 16,
  },
  subtitle: {
    color: theme.colors.inactiveText,
    textAlign: 'left',
  },
  title: {
    textAlign: 'left',
  },
  webMobileMaxHeight: {
    maxHeight: '80%', // To keep all of mobile action sheet on screen
  },
}));
